import type { Id } from "@repo/convex/dataModel";
import { useEffect, useMemo } from "react";
import {
  useClarificationRequests,
  useClarificationsByEntity,
} from "@/features/clarification/hooks";
import { resolveClarificationDeepLinkView } from "@/features/clarification/lib/clarification-deep-link";
import { resolveClarificationAuthorDisplay } from "@/features/clarification/utils/author-display";
import {
  useRightSidebarClarification,
  useRightSidebarEntity,
  useRightSidebarProject,
} from "@/features/project/contexts";
import { useCurrentUser, useOrgUserMap } from "@/hooks";
import { trackClarificationEvent } from "@/lib/analytics/domain";
import { ClarificationCreateForm } from "./clarification-create-form";
import { ClarificationListView } from "./clarification-list";
import { ClarificationThread } from "./clarification-thread";

type MessageSource = "app" | "teams" | "claudia";
type ClarificationStatus = "open" | "closed";

export interface ClarificationWithDates {
  id: string;
  title: string;
  status: ClarificationStatus;
  author: string;
  authorInitials: string;
  createdAt: Date;
  lastActivity: Date;
  fieldId?: string;
  highlightedText?: string;
  ownerId?: Id<"users">;
  messages: Array<{
    id: string;
    content: string;
    author: string;
    authorInitials: string;
    timestamp: Date;
    source: MessageSource;
  }>;
}

export function ClarificationRequestsPanel() {
  const { projectId } = useRightSidebarProject();
  const { entityType, entityId, entityName, isEntityContextSettledForRoute } =
    useRightSidebarEntity();
  const {
    clarificationFilter: filter,
    setClarificationFilter: onFilterChange,
    clarificationViewMode,
    selectedClarificationId,
    isCreatingClarification,
    linkedClarificationActivationKey,
    linkedPrimaryClarificationId,
    linkedClarificationIds,
    openClarificationList,
    openClarificationThread,
  } = useRightSidebarClarification();

  const isEntityScoped = Boolean(entityType && entityId);

  useEffect(() => {
    trackClarificationEvent("clarifications_opened", {
      surface: "right_panel",
      project_id: projectId,
    });
  }, [projectId]);

  // Fetch clarifications — entity-scoped when viewing an entity, project-wide otherwise
  const entityClarificationsData = useClarificationsByEntity(
    isEntityScoped ? projectId : null,
    entityType,
    entityId
  );
  const projectClarificationsData = useClarificationRequests(isEntityScoped ? null : projectId);
  const clarificationsData = isEntityScoped ? entityClarificationsData : projectClarificationsData;

  // Single source of truth for thread-vs-list (and whether a stale selection may be
  // cleared). The right sidebar requires crv="t" to show a thread; a transient scoped-list
  // miss during a cold notification navigation resolves to `pending`, never `list`, and
  // never rewrites the URL — only an authoritative, route-settled miss clears it. LAT-575.
  const deepLink = resolveClarificationDeepLinkView({
    policy: "requiresThreadViewParam",
    viewMode: clarificationViewMode,
    selectedId: selectedClarificationId,
    isCreating: isCreatingClarification,
    scopedData: clarificationsData,
    contextSettled: isEntityContextSettledForRoute,
  });

  const shouldClearStaleSelection = deepLink.shouldClearStaleSelection;
  useEffect(() => {
    if (shouldClearStaleSelection) {
      // Authoritative stale selection: drop it back to list mode. This is the only path
      // that mutates the deep-link URL params.
      openClarificationList();
    }
  }, [shouldClearStaleSelection, openClarificationList]);

  const userMap = useOrgUserMap();
  const currentUser = useCurrentUser();

  // Transform Convex data to component format
  const clarifications = useMemo((): ClarificationWithDates[] => {
    if (!clarificationsData) return [];

    return clarificationsData.map((q) => {
      const author = resolveClarificationAuthorDisplay({
        createdBy: q.createdBy,
        currentUser,
        userMap,
        source: "app",
      });

      return {
        id: q._id,
        title: q.title,
        status: q.status as ClarificationStatus,
        author: author.label,
        authorInitials: author.initials,
        createdAt: new Date(q._creationTime),
        lastActivity: new Date(q.lastActivityAt),
        fieldId: q.fieldId,
        highlightedText: q.blockReference?.highlightedText,
        ownerId: q.ownerId,
        messages: [], // Messages loaded separately when a clarification is selected
      };
    });
  }, [clarificationsData, userMap, currentUser]);

  const handleBack = () => {
    openClarificationList(selectedClarificationId);
  };

  // ============================================================================
  // Create Form (New Clarification)
  // ============================================================================

  if (isCreatingClarification) {
    return <ClarificationCreateForm onBack={handleBack} />;
  }

  // ============================================================================
  // Pending: data loading or scope not yet authoritative — keep the deep-link intent
  // and wait, rather than committing to the list (which would strand a cold-navigated
  // thread). LAT-575.
  // ============================================================================

  if (deepLink.mode === "pending") {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-sm text-muted-foreground">Loading clarification requests...</p>
      </div>
    );
  }

  // ============================================================================
  // Chat View
  // ============================================================================

  if (deepLink.mode === "thread") {
    return (
      <ClarificationThread
        projectId={projectId}
        clarificationRequestId={deepLink.clarificationId as Id<"clarificationRequests">}
        onBack={handleBack}
        surface="right_panel"
      />
    );
  }

  // ============================================================================
  // List View
  // ============================================================================

  return (
    <ClarificationListView
      clarifications={clarifications}
      filter={filter}
      onFilterChange={onFilterChange}
      onClarificationOpen={openClarificationThread}
      linkedActivationKey={linkedClarificationActivationKey}
      linkedPrimaryClarificationId={linkedPrimaryClarificationId}
      linkedClarificationIds={linkedClarificationIds}
      groupByField={isEntityScoped}
      entityName={entityName}
    />
  );
}
